import { SweepMesh } from '../geometry/sweep';

/** Fixed 1:100 — world metres → print mm via worldMetersToPrintMm. */
export const PRINT_SCALE = 100;

export interface PrintSettings {
  /** Cubic build-volume side length in millimetres. */
  chunkSizeMm: number;
  /**
   * Minimum arc distance (print mm) between a track cut and a support
   * connection. Cuts are snapped away from attaches so stub + elbow stay
   * fully inside one track chunk.
   */
  supportCutClearanceMm: number;
  /** When true, track supports are exported as separate parts. */
  separateSupport: boolean;
  /**
   * When true, track chunk cuts snap to the nearest gear-rack tooth bottom
   * (valley at n·pitch). Off: rack teeth are clipped to the track cut.
   */
  splitAtToothBottom: boolean;
  /** Round HSS bolted endplates at mid-beam splits and column↔footer joints. */
  hssEndplateEnabled: boolean;
  /** HSS flange plate thickness in print millimetres (footer / general endplates). */
  hssEndplateThicknessMm: number;
  /** HSS outer diameter in print millimetres (footer / general endplates). */
  hssEndplateDiameterMm: number;
  /** HSS bolts equally spaced around the flange. */
  hssEndplateBoltCount: number;
  /** HSS through-hole diameter in print millimetres. */
  hssEndplateBoltHoleMm: number;
  /**
   * HSS bolt pitch on the flange: 0 = toward the tube (center), 1 = toward the
   * plate rim (edge). Clamped to keep holes on the lip.
   */
  hssEndplateBoltCircle: number;
  /**
   * Max straight beam length in print millimetres. Longer columns/braces/knees
   * are cut into equal segments with HSSS endplates at each splice.
   * 0 = no max-length splits.
   */
  maxBeamLengthMm: number;
  /** HSSS flange thickness in print millimetres (mid-beam splices). */
  hssSplitThicknessMm: number;
  /** HSSS outer diameter in print millimetres (mid-beam splices). */
  hssSplitDiameterMm: number;
  /** HSSS bolt count (mid-beam splices). */
  hssSplitBoltCount: number;
  /** HSSS through-hole diameter in print millimetres. */
  hssSplitBoltHoleMm: number;
  /** HSSS bolt pitch: 0 = center, 1 = rim. */
  hssSplitBoltCircle: number;
  /** Footer pad thickness in print millimetres. */
  footerThicknessMm: number;
  /** Footer pad full side length in print millimetres. */
  footerSizeMm: number;
  /**
   * Footer vertical HSS stub standing on the pad (print mm). 0 = column sits on
   * the plate. Applied after pad thickness so the stub sits fully above the pad.
   */
  footerAttachStubMm: number;
  /** When true, Generate also exports a chunked ground plane under the coaster. */
  groundPlaneEnabled: boolean;
  /** Ground-plane slab thickness in print millimetres (clips sit on top). */
  groundPlaneHeightMm: number;
  /**
   * Butterfly / bow-tie key pocket depth in print millimetres, cut down from
   * the slab top. May be less than slab height (blind pocket) or ≥ slab height
   * (through cut).
   */
  groundButterflyDepthMm: number;
}

export const DEFAULT_PRINT_SETTINGS: PrintSettings = {
  chunkSizeMm: 200,
  supportCutClearanceMm: 15,
  separateSupport: false,
  splitAtToothBottom: false,
  hssEndplateEnabled: true,
  hssEndplateThicknessMm: 0.8,
  hssEndplateDiameterMm: 9.5,
  hssEndplateBoltCount: 6,
  hssEndplateBoltHoleMm: 0.6,
  hssEndplateBoltCircle: 0.55,
  maxBeamLengthMm: 80,
  hssSplitThicknessMm: 0.4,
  hssSplitDiameterMm: 9.5,
  hssSplitBoltCount: 6,
  hssSplitBoltHoleMm: 0.3,
  hssSplitBoltCircle: 0.55,
  footerThicknessMm: 2.5,
  footerSizeMm: 10,
  footerAttachStubMm: 0,
  groundPlaneEnabled: true,
  groundPlaneHeightMm: 2,
  groundButterflyDepthMm: 1.2,
};

export type PrintPartKind = 'track' | 'support' | 'ground' | 'connector';

export interface PrintPart {
  id: string;
  kind: PrintPartKind;
  /** Arc-length interval for track parts; approximate for support packs. */
  s0: number;
  s1: number;
  /** Mesh in world metres (preview). STL export scales to print mm. */
  mesh: SweepMesh;
  /** Axis-aligned extents in print mm after scale (max side used for fit checks). */
  obbMm: { x: number; y: number; z: number };
  /** True when the part exceeds the build cube after every split (unsplittable). */
  oversized: boolean;
  /** True when Manifold STL fixer produced a solid (false = cleaned triangle soup fallback). */
  fixed: boolean;
  /** Optional fixer note (skipped pieces, cavity skip, etc.). */
  fixWarning?: string;
}

/**
 * One split fragment before packing into a print chunk (track interval, hub,
 * half-beam, footer, …). Preview-only — not a downloadable solid.
 */
export interface PrintAtom {
  id: string;
  /** Print chunk this atom was packed into (`track-001` / `support-001`). */
  chunkId: string;
  kind: PrintPartKind;
  mesh: SweepMesh;
}

export interface PrintJob {
  settings: PrintSettings;
  /** Interior cut positions along the track (excludes 0 and totalLength). */
  cuts: number[];
  parts: PrintPart[];
  /** Split fragments shown in the "parts" print preview. */
  atoms: PrintAtom[];
  warnings: string[];
  /** Diagnostic lines from the generate pipeline (copy/paste friendly). */
  log: string[];
}

/** Convert a world-space length/coordinate (metres) to print millimetres at PRINT_SCALE. */
export function worldMetersToPrintMm(metres: number): number {
  return (metres / PRINT_SCALE) * 1000;
}

/** Convert print millimetres back to world metres at PRINT_SCALE. */
export function printMmToWorldMetres(mm: number): number {
  return (mm * PRINT_SCALE) / 1000;
}

export function normalizePrintSettings(
  partial:
    | (Partial<PrintSettings> & {
        footerSizeScale?: number;
        beamSplitHssScale?: number;
        hssEndplateOverhangMm?: number;
        hssEndplateSizeScale?: number;
      })
    | null
    | undefined,
): PrintSettings {
  const legacySplitScale =
    partial?.beamSplitHssScale != null && Number.isFinite(partial.beamSplitHssScale)
      ? Math.max(partial.beamSplitHssScale, 0.01)
      : null;
  const baseThickness = clampPrintNum(
    partial?.hssEndplateThicknessMm,
    DEFAULT_PRINT_SETTINGS.hssEndplateThicknessMm,
    0.3,
    3,
  );
  const baseHole = clampPrintNum(
    partial?.hssEndplateBoltHoleMm,
    DEFAULT_PRINT_SETTINGS.hssEndplateBoltHoleMm,
    0.3,
    2,
  );
  const baseBoltCount = Math.round(
    clampPrintNum(
      partial?.hssEndplateBoltCount,
      DEFAULT_PRINT_SETTINGS.hssEndplateBoltCount,
      4,
      12,
    ),
  );
  const baseCircle = clampPrintNum(
    partial?.hssEndplateBoltCircle,
    DEFAULT_PRINT_SETTINGS.hssEndplateBoltCircle,
    0,
    1,
  );

  return {
    chunkSizeMm:
      partial?.chunkSizeMm != null && Number.isFinite(partial.chunkSizeMm)
        ? Math.min(350, Math.max(80, partial.chunkSizeMm))
        : DEFAULT_PRINT_SETTINGS.chunkSizeMm,
    supportCutClearanceMm:
      partial?.supportCutClearanceMm != null && Number.isFinite(partial.supportCutClearanceMm)
        ? Math.min(50, Math.max(0, partial.supportCutClearanceMm))
        : DEFAULT_PRINT_SETTINGS.supportCutClearanceMm,
    separateSupport: partial?.separateSupport === true,
    splitAtToothBottom: partial?.splitAtToothBottom === true,
    hssEndplateEnabled: partial?.hssEndplateEnabled !== false,
    hssEndplateThicknessMm: baseThickness,
    hssEndplateDiameterMm: resolveHssDiameterMm(partial),
    hssEndplateBoltCount: baseBoltCount,
    hssEndplateBoltHoleMm: baseHole,
    hssEndplateBoltCircle: baseCircle,
    maxBeamLengthMm: clampPrintNum(
      partial?.maxBeamLengthMm,
      DEFAULT_PRINT_SETTINGS.maxBeamLengthMm,
      0,
      350,
    ),
    hssSplitThicknessMm: clampPrintNum(
      partial?.hssSplitThicknessMm,
      legacySplitScale != null ? baseThickness * legacySplitScale : DEFAULT_PRINT_SETTINGS.hssSplitThicknessMm,
      0.15,
      3,
    ),
    hssSplitDiameterMm: clampPrintNum(
      partial?.hssSplitDiameterMm,
      legacySplitScale != null ? 9.5 * (legacySplitScale / 0.5) : DEFAULT_PRINT_SETTINGS.hssSplitDiameterMm,
      4,
      40,
    ),
    hssSplitBoltCount: Math.round(
      clampPrintNum(
        partial?.hssSplitBoltCount,
        baseBoltCount,
        4,
        12,
      ),
    ),
    hssSplitBoltHoleMm: clampPrintNum(
      partial?.hssSplitBoltHoleMm,
      legacySplitScale != null ? baseHole * legacySplitScale : DEFAULT_PRINT_SETTINGS.hssSplitBoltHoleMm,
      0.15,
      2,
    ),
    hssSplitBoltCircle: clampPrintNum(
      partial?.hssSplitBoltCircle,
      baseCircle,
      0,
      1,
    ),
    footerThicknessMm: clampPrintNum(
      partial?.footerThicknessMm,
      DEFAULT_PRINT_SETTINGS.footerThicknessMm,
      1.2,
      8,
    ),
    footerSizeMm: resolveFooterSizeMm(partial),
    footerAttachStubMm: clampPrintNum(
      partial?.footerAttachStubMm,
      DEFAULT_PRINT_SETTINGS.footerAttachStubMm,
      0,
      15,
    ),
    groundPlaneEnabled: partial?.groundPlaneEnabled !== false,
    groundPlaneHeightMm: clampPrintNum(
      partial?.groundPlaneHeightMm,
      DEFAULT_PRINT_SETTINGS.groundPlaneHeightMm,
      0.8,
      8,
    ),
    groundButterflyDepthMm: clampPrintNum(
      partial?.groundButterflyDepthMm,
      DEFAULT_PRINT_SETTINGS.groundButterflyDepthMm,
      0.4,
      8,
    ),
  };
}

/** Absolute HSS diameter (mm); migrate legacy overhang × sizeScale when needed. */
function resolveHssDiameterMm(
  partial:
    | (Partial<PrintSettings> & {
        hssEndplateOverhangMm?: number;
        hssEndplateSizeScale?: number;
      })
    | null
    | undefined,
): number {
  if (partial?.hssEndplateDiameterMm != null && Number.isFinite(partial.hssEndplateDiameterMm)) {
    return clampPrintNum(
      partial.hssEndplateDiameterMm,
      DEFAULT_PRINT_SETTINGS.hssEndplateDiameterMm,
      4,
      40,
    );
  }
  const overhang = partial?.hssEndplateOverhangMm;
  if (overhang != null && Number.isFinite(overhang)) {
    const scale =
      partial?.hssEndplateSizeScale != null && Number.isFinite(partial.hssEndplateSizeScale)
        ? Math.max(partial.hssEndplateSizeScale, 0.01)
        : 1;
    // Former default lip on a typical 0.22 m column radius.
    const diam = 2 * (worldMetersToPrintMm(0.22) + overhang * scale);
    return clampPrintNum(diam, DEFAULT_PRINT_SETTINGS.hssEndplateDiameterMm, 4, 40);
  }
  return DEFAULT_PRINT_SETTINGS.hssEndplateDiameterMm;
}

/** Absolute pad side (mm); migrate legacy `footerSizeScale` when needed. */
function resolveFooterSizeMm(
  partial: (Partial<PrintSettings> & { footerSizeScale?: number }) | null | undefined,
): number {
  if (partial?.footerSizeMm != null && Number.isFinite(partial.footerSizeMm)) {
    return clampPrintNum(partial.footerSizeMm, DEFAULT_PRINT_SETTINGS.footerSizeMm, 5, 40);
  }
  const scale = partial?.footerSizeScale;
  if (scale != null && Number.isFinite(scale)) {
    // Former default: columnRadius(0.22) × 2.2 half-side at scale 1 ≈ 9.7 mm full side.
    const baseHalfM = 0.22 * 2.2;
    const fullMm = 2 * worldMetersToPrintMm(baseHalfM) * scale;
    return clampPrintNum(fullMm, DEFAULT_PRINT_SETTINGS.footerSizeMm, 5, 40);
  }
  return DEFAULT_PRINT_SETTINGS.footerSizeMm;
}

function clampPrintNum(
  value: number | undefined,
  fallback: number,
  min: number,
  max: number,
): number {
  if (value == null || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}
